import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Briefcase, Clock, ArrowRight, ArrowLeft, CheckCircle2, UploadCloud, Search } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const field = "mt-1.5 focus-visible:ring-emerald-brand";

export default function Jobs() {
  const { id } = useParams();
  const [content, setContent] = useState(null);
  useEffect(() => { api.get("/content").then((r) => setContent(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <Header />
      {id ? <JobDetail id={id} /> : <JobList />}
      <Footer content={content} />
    </div>
  );
}

function JobList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const filtered = jobs.filter((j) => JSON.stringify(j).toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <section className="bg-navy text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 grain-band opacity-30" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <span className="text-xs tracking-[0.25em] uppercase text-emerald-light font-medium">Careers</span>
          <h1 className="mt-3 font-display font-bold text-4xl lg:text-5xl tracking-tight">Open Positions</h1>
          <p className="mt-3 text-slate-300 max-w-2xl">Explore current openings and apply to the role that fits you best. Don't see a match? <Link to="/submit-resume" className="text-emerald-light underline">Submit a general resume</Link>.</p>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative max-w-md mb-8">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input data-testid="jobs-search" placeholder="Search roles, location…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 focus-visible:ring-emerald-brand bg-white" />
          </div>
          {loading ? <p className="text-slate-500">Loading openings…</p> : filtered.length === 0 ? (
            <div data-testid="jobs-empty" className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
              <Briefcase className="mx-auto text-slate-300" size={40} />
              <p className="mt-4 text-slate-600">No open positions right now. Please check back soon or submit a general resume.</p>
              <Link to="/submit-resume" className="inline-block mt-5 bg-emerald-brand text-white font-semibold rounded-full px-6 py-3 hover:bg-emerald-dark transition-colors">Submit Resume</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {filtered.map((j, i) => (
                <motion.div key={j.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  data-testid={`job-card-${i}`} className="group bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display font-semibold text-xl text-navy">{j.title}</h3>
                      {j.department && <span className="text-sm text-emerald-brand font-medium">{j.department}</span>}
                    </div>
                    <span className="shrink-0 text-xs font-medium bg-navy/5 text-navy rounded-full px-3 py-1">{j.job_type}</span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600">
                    {j.location && <span className="flex items-center gap-1.5"><MapPin size={15} className="text-emerald-brand" /> {j.location}</span>}
                    {j.experience && <span className="flex items-center gap-1.5"><Clock size={15} className="text-emerald-brand" /> {j.experience}</span>}
                  </div>
                  {j.description && <p className="mt-3 text-sm text-slate-600 line-clamp-2">{j.description}</p>}
                  <Link to={`/jobs/${j.id}`} data-testid={`job-view-${j.id}`} className="mt-5 inline-flex items-center gap-1.5 text-navy font-semibold group-hover:text-emerald-brand transition-colors">View & Apply <ArrowRight size={16} /></Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function JobDetail({ id }) {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setErr(true)); }, [id]);

  if (err) return (
    <section className="py-24 text-center"><p className="text-slate-600">This position is no longer available.</p>
      <Link to="/jobs" className="inline-block mt-4 text-emerald-brand font-medium">Back to all jobs</Link></section>
  );
  if (!job) return <section className="py-24 text-center text-slate-500">Loading…</section>;

  return (
    <>
      <section className="bg-navy text-white py-14">
        <div className="max-w-4xl mx-auto px-6">
          <button onClick={() => navigate("/jobs")} className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white mb-4"><ArrowLeft size={16} /> All Openings</button>
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tight">{job.title}</h1>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-300">
            {job.department && <span className="flex items-center gap-1.5"><Briefcase size={15} className="text-emerald-light" /> {job.department}</span>}
            {job.location && <span className="flex items-center gap-1.5"><MapPin size={15} className="text-emerald-light" /> {job.location}</span>}
            {job.experience && <span className="flex items-center gap-1.5"><Clock size={15} className="text-emerald-light" /> {job.experience}</span>}
            <span className="bg-white/10 rounded-full px-3 py-0.5">{job.job_type}</span>
          </div>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-4xl mx-auto px-6 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <h2 className="font-display font-semibold text-2xl text-navy mb-4">About the Role</h2>
            <p className="text-slate-600 leading-relaxed whitespace-pre-line">{job.description || "Details will be shared during the interview process."}</p>
          </div>
          <div className="lg:col-span-2">
            <JobApply jobId={job.id} jobTitle={job.title} />
          </div>
        </div>
      </section>
    </>
  );
}

function JobApply({ jobId, jobTitle }) {
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ full_name: "", email: "", phone: "", experience: "", current_location: "", expected_ctc: "", notice_period: "" });
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const pick = (e) => {
    const sel = e.target.files?.[0];
    if (!sel) return;
    const ext = sel.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) { toast.error("Only PDF, DOC or DOCX allowed"); return; }
    if (sel.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setFile(sel);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please upload your resume"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("job_id", jobId); fd.append("job_title", jobTitle); fd.append("resume", file);
      await api.post("/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Application submitted!");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Submission failed"); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div data-testid="job-apply-success" className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
      <CheckCircle2 className="mx-auto text-emerald-brand" size={48} />
      <h3 className="mt-3 font-display font-semibold text-xl text-navy">Applied!</h3>
      <p className="mt-2 text-sm text-slate-600">Thanks for applying to {jobTitle}. We'll be in touch if there's a match.</p>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="job-apply-form" className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 sticky top-24">
      <h3 className="font-display font-semibold text-lg text-navy">Apply for this role</h3>
      <div><Label>Full Name *</Label><Input required data-testid="apply-name" className={field} value={f.full_name} onChange={on("full_name")} /></div>
      <div><Label>Email *</Label><Input required type="email" data-testid="apply-email" className={field} value={f.email} onChange={on("email")} /></div>
      <div><Label>Phone *</Label><Input required data-testid="apply-phone" className={field} value={f.phone} onChange={on("phone")} /></div>
      <div><Label>Experience *</Label><Input required data-testid="apply-experience" className={field} placeholder="e.g. 4 years" value={f.experience} onChange={on("experience")} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Expected CTC</Label><Input className={field} value={f.expected_ctc} onChange={on("expected_ctc")} /></div>
        <div><Label>Notice</Label><Input className={field} value={f.notice_period} onChange={on("notice_period")} /></div>
      </div>
      <div>
        <Label>Resume (PDF/DOC/DOCX) *</Label>
        <label data-testid="apply-dropzone" className="mt-1.5 flex flex-col items-center gap-1.5 border-2 border-dashed border-slate-300 rounded-xl py-5 cursor-pointer hover:border-emerald-brand transition-colors">
          <UploadCloud className="text-emerald-brand" size={22} />
          <span className="text-xs text-slate-600 px-2 text-center">{file ? file.name : "Upload resume"}</span>
          <input type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid="apply-file" onChange={pick} />
        </label>
      </div>
      <Button type="submit" disabled={loading} data-testid="apply-submit" className="w-full bg-emerald-brand hover:bg-emerald-dark rounded-full py-6 font-semibold">{loading ? "Submitting…" : "Submit Application"}</Button>
    </form>
  );
}
